import { useEffect, useState } from "react";
import { UserProfile } from "../types";
import DayHeroBannerImage from "../assets/homepage/DayHeroBanner.svg";
import NightHeroBannerImage from "../assets/homepage/NightHeroBanner.svg";
import backgroundVideo from "../assets/video/gradient-video.mp4";
import {
  DayPeriod,
  getDayPeriod,
  getMillisecondsUntilNextTransition,
} from "../utils/dayNight";
import { WELCOME_MESSAGES } from "../constants/WelcomeMessage";
import AvatarStack from "../components/AvatarStack";
import CokeStudioPlaylistWidget from "../components/CokeStudioWidget/CokeStudioPlaylistWidget";
import IdeaOfTheMonthWidget from "src/components/IdeaOfTheMonthWidget/IdeaOfTheMonthWidget";
import MonthlyPickWidget from "../components/MonthlyPickWidget/MonthlyPickWidget";
import NewsModule from "src/components/NewsModule/NewsModule";
import IdeaCarousel from "src/components/IdeaModule/IdeaCarousel";
interface HomePageProps {
  username: string;
  userData?: UserProfile;
}

const getRandomWelcomePrefix = (): string => {
  const randomMessage =
    WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
  return randomMessage.split("{name}")[0]?.trim() || "Good to see you again,";
};

const HomePage = ({ username, userData }: HomePageProps) => {
  const [welcomePrefix, setWelcomePrefix] = useState("");
  const [backgroundColor, setBackgroundColor] = useState<DayPeriod>(() =>
    getDayPeriod(),
  );

  const displayName = userData?.firstName;

  useEffect(() => {
    setWelcomePrefix(getRandomWelcomePrefix());
  }, [displayName]);

  useEffect(() => {
    const updateTheme = () => setBackgroundColor(getDayPeriod());

    let timeoutId = 0;

    const scheduleNextUpdate = () => {
      updateTheme();
      timeoutId = window.setTimeout(
        scheduleNextUpdate,
        getMillisecondsUntilNextTransition(),
      );
    };

    scheduleNextUpdate();

    return () => window.clearTimeout(timeoutId);
  }, []);

  const heroImage =
    backgroundColor === "day" ? DayHeroBannerImage : NightHeroBannerImage;

  return (
    <div className="relative w-full bg-[#ffffff] overflow-hidden">
      <img
        src={heroImage}
        className="absolute inset-0 w-full object-cover object-center image-height"
      />

      <div className="absolute inset-0 bg-white/18" />

      <main className="app-shell-inner ">
        <div className="relative z-[2] grid grid-cols-1 lg:grid-cols-12">
          <section className="flex lg:col-span-5 mt-[170px]">
            <div className="w-full px-2 py-10 md:px-4 lg:px-0 lg:py-0">
              <div className="w-[397px]">
                <div className="text-balance font-normal text-[56px] leading-[64px]">
                  <span
                    className={`block ${backgroundColor === "day" ? "text-color-neutral-80" : "text-color-neutral-60"}`}
                  >
                    {welcomePrefix}
                  </span>
                  <span
                    className={`mt-1 flex flex-wrap gap-x-4 gap-y-2 font-medium flex-baseline-last ${backgroundColor === "day" ? "text-color-neutral-100" : "text-color-neutral-10"}`}
                  >
                    <span>{displayName}</span>

                    <AvatarStack
                      firstName={userData?.firstName}
                      lastName={userData?.lastName}
                      avatarUrl={userData?.avatarUrl}
                    />
                  </span>
                </div>
              </div>
            </div>
          </section>
          <div className="lg:col-span-1"></div>
          <div className="lg:col-span-6 flex flex-col gap-4">
            <div
              className={`h-[462px] w-[796px] mt-[182px] relative rounded-[44px] right-0 mr-[-137px] p-6 drop-shadow-[0_4px_8px_rgba(0,0,0,0.10)] overflow-hidden,
        ${
          backgroundColor === "day"
            ? "bg-[rgba(255,255,255,0.50)]"
            : "bg-[rgba(37,37,37,0.5)]"
        }`}
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute top-1/2 left-1/2 h-[414px] -translate-x-1/2 -translate-y-1/2 object-cover z-[1] rounded-[24px] opacity-25"
              >
                <source src={backgroundVideo} type="video/mp4" />
              </video>
              <div className="relative z-[2] text-[#ffffff] text-center mt-[-172px]">
                <div className="lg:col-span-6 flex flex-col gap-4">
                  <div className="relative lg:grid lg:grid-cols-6 lg:col-span-6 gap-4 mt-[34px]">
                    <div className="lg:col-span-2 flex justify-end lg:mb-0">
                      <IdeaOfTheMonthWidget
                        mode={backgroundColor === "day" ? "light" : "dark"}
                      />
                    </div>
                    <div className="lg:col-span-3 z-10 flex justify-start mb-4 lg:mb-0">
                      <CokeStudioPlaylistWidget
                        spotifyPlaylistUrl="https://open.spotify.com/embed/playlist/1mgYJ5fxzgg3AX99VBVhju?si=gG6s-H23QA65FVn9QKbyrg&nd=1&dlsi=7fd74daae3dd41bb?utm_source=generator"
                        mode={backgroundColor === "day" ? "light" : "dark"}
                      />
                    </div>
                    <div className="lg:col-span-1"></div>
                  </div>

                  {/* Monthly Pick Widget Section */}
                  <div className="relative lg:grid lg:grid-cols-5 lg:col-span-5 gap-4">
                    <div className="lg:col-span-4 w-full z-10 flex justify-start">
                      <MonthlyPickWidget
                        mode={backgroundColor === "day" ? "light" : "dark"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="col-span-full
    absolute left-1/2  w-screen -translate-x-1/2 z-[2] -mt-[55px] min-h-[873px] rounded-[24px] bg-white shadow-[0_-4px_16px_0_rgba(0,0,0,0.08)]"
        ></div>
        <div className="relative grid grid-cols-1 lg:grid-cols-12 w-full z-[2]">
          <NewsModule className="relative" />
        </div>
        <div className="relative grid grid-cols-12 lg:grid-cols-12 z-[2]">
          <IdeaCarousel userData={userData}/>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
